<script setup>
import {
  applyUpdate,
  canInstall,
  isIos,
  isOnline,
  needRefresh,
  promptInstall,
  showIosInstallHelp,
} from '@/pwa'

// "Not now" on the install banner hides it for this many days.
const INSTALL_DISMISS_KEY = 'pwa-install-dismissed-at'
const INSTALL_DISMISS_DAYS = 14
const INSTALL_BANNER_DELAY = 5000

const isBackOnlineVisible = ref(false)
const isInstallBannerReady = ref(false)
const isInstallDismissed = ref(false)

const readDismissed = () => {
  try {
    const at = Number(localStorage.getItem(INSTALL_DISMISS_KEY) || 0)

    return at > 0 && Date.now() - at < INSTALL_DISMISS_DAYS * 24 * 60 * 60 * 1000
  }
  catch {
    return false
  }
}

const isInstallBannerVisible = computed({
  get: () => isInstallBannerReady.value && canInstall.value && !isInstallDismissed.value && isOnline.value && !needRefresh.value,
  set: value => {
    if (!value)
      dismissInstall()
  },
})

const isOfflineVisible = computed({
  get: () => !isOnline.value,
  set: () => {},
})

const dismissInstall = () => {
  isInstallDismissed.value = true
  try {
    localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now()))
  }
  catch {
    // ignore
  }
}

const install = async () => {
  isInstallDismissed.value = true
  await promptInstall()
}

watch(isOnline, (online, wasOnline) => {
  if (online && wasOnline === false)
    isBackOnlineVisible.value = true
})

let bannerTimer = null

onMounted(() => {
  isInstallDismissed.value = readDismissed()
  bannerTimer = setTimeout(() => { isInstallBannerReady.value = true }, INSTALL_BANNER_DELAY)
})

onBeforeUnmount(() => clearTimeout(bannerTimer))
</script>

<template>
  <!-- 👉 Offline -->
  <VSnackbar
    v-model="isOfflineVisible"
    location="top"
    color="error"
    :timeout="-1"
    variant="flat"
  >
    <div class="d-flex align-center gap-2">
      <VIcon
        icon="tabler-wifi-off"
        size="20"
      />
      You're offline. Data can't be loaded or saved until the connection is back.
    </div>
  </VSnackbar>

  <VSnackbar
    v-model="isBackOnlineVisible"
    location="top"
    color="success"
    :timeout="3000"
    variant="flat"
  >
    You're back online.
  </VSnackbar>

  <!-- 👉 New version -->
  <VSnackbar
    v-model="needRefresh"
    location="bottom end"
    :timeout="-1"
    variant="flat"
    color="primary"
    vertical
  >
    <div class="d-flex align-center gap-2">
      <VIcon
        icon="tabler-refresh"
        size="20"
      />
      A new version of the app is available.
    </div>

    <template #actions>
      <VBtn
        variant="text"
        @click="needRefresh = false"
      >
        Later
      </VBtn>
      <VBtn
        variant="elevated"
        color="white"
        @click="applyUpdate"
      >
        Reload
      </VBtn>
    </template>
  </VSnackbar>

  <!-- 👉 Install -->
  <VSnackbar
    v-model="isInstallBannerVisible"
    location="bottom start"
    :timeout="-1"
    variant="flat"
    color="surface"
    vertical
    class="pwa-install-banner"
  >
    <div class="d-flex align-center gap-3">
      <VAvatar
        rounded
        size="40"
        image="/icons/icon-192.png"
      />
      <div>
        <div class="text-body-1 font-weight-medium text-high-emphasis">
          Install PrintingERP
        </div>
        <div class="text-body-2">
          Open it from your home screen or desktop like a regular app.
        </div>
      </div>
    </div>

    <template #actions>
      <VBtn
        variant="text"
        color="secondary"
        @click="dismissInstall"
      >
        Not now
      </VBtn>
      <VBtn
        variant="elevated"
        color="primary"
        prepend-icon="tabler-download"
        @click="install"
      >
        Install
      </VBtn>
    </template>
  </VSnackbar>

  <!-- 👉 iOS instructions (Safari has no install dialog) -->
  <VDialog
    v-if="isIos"
    v-model="showIosInstallHelp"
    max-width="400"
  >
    <VCard title="Install PrintingERP">
      <VCardText>
        <ol class="ps-4">
          <li class="mb-2">
            Tap the <VIcon
              icon="tabler-share-2"
              size="18"
            /> <strong>Share</strong> button in Safari.
          </li>
          <li class="mb-2">
            Choose <VIcon
              icon="tabler-square-plus"
              size="18"
            /> <strong>Add to Home Screen</strong>.
          </li>
          <li>Tap <strong>Add</strong>.</li>
        </ol>
      </VCardText>
      <VCardActions>
        <VSpacer />
        <VBtn
          color="primary"
          @click="showIosInstallHelp = false"
        >
          Got it
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.pwa-install-banner :deep(.v-snackbar__wrapper) {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
